import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useAuth, useClerk } from "@clerk/tanstack-react-start";
import z from "zod";

export const Route = createFileRoute("/_auth/accept-token/")({
  validateSearch: z.object({
    token: z.string(),
    redirect: z.string().optional(),
  }),
  component: AcceptTokenPage,
});

type AcceptTokenStatus = "signing-in" | "redirecting-to-login";

function AcceptTokenPage() {
  const [status, setStatus] = useState<AcceptTokenStatus>("signing-in");
  const search = useSearch({ from: "/_auth/accept-token/" });
  const token = (search?.token as string | undefined) ?? undefined;
  const redirect =
    (search?.redirect as string | undefined) ?? "/settings/password";

  const { client, setActive } = useClerk();
  const { isSignedIn } = useAuth();

  const redirectToLogin = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    setStatus("redirecting-to-login");

    const loginUrl = new URL("/signin", window.location.origin);
    loginUrl.searchParams.set("from", "accept-token-failure");

    if (redirect) {
      loginUrl.searchParams.set("redirect", redirect);
    }

    window.location.replace(loginUrl.toString());
  }, [redirect]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // If the user is already signed in, just go to the redirect target
      if (isSignedIn && redirect) {
        window.location.replace(redirect);
        return;
      }

      if (!token) {
        redirectToLogin();
        return;
      }

      if (!client) return;
      try {
        const attempt = await client.signIn.create({
          strategy: "ticket",
          ticket: token,
        });

        if (attempt.status === "complete" && attempt.createdSessionId) {
          await setActive({ session: attempt.createdSessionId });
          if (!cancelled) {
            window.location.replace(redirect);
          }
          return;
        }
        redirectToLogin();
      } catch {
        redirectToLogin();
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, client, setActive, redirect, isSignedIn, redirectToLogin]);

  return (
    <div className="mx-auto max-w-md p-8">
      {status === "signing-in"
        ? "Signing you in…"
        : "We couldn't automatically sign you in. Redirecting to the login page…"}
    </div>
  );
}
